/* eslint-disable max-lines */
import {
  Table,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useContext, useState, useEffect, useRef, ChangeEvent } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import PolicyViolationsService from '../../../services/PolicyViolations';
import { toast } from 'react-toastify';
import ModalContent from '../../../components/Modals/Modal';

type PolicyViolationsFormProps = {
  isOpen: boolean;
};

const PolicyViolationsForm = ({ isOpen }: PolicyViolationsFormProps) => {
  const { token }: any = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [policyViolationsName, setPolicyViolationsName] = useState('');
  const [policyViolationsNames, setPolicyViolationsNames] = useState('');
  const [policyViolationsList, setPolicyViolationsList] = useState([] as any[]);
  const [selectedFile, setSelectedFile] = useState<any>();
  const [editId, setEditId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCustomButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectFile = event.target.files?.[0];
    if (selectFile) {
      setSelectedFile(selectFile);
    }
  };

  useEffect(() => {
    if (isOpen) {
      listData();
    }
  }, [isOpen]);

  const listData = async () => {
    try {
      const result = await PolicyViolationsService(token).list();

      setPolicyViolationsList(result.data || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No data Found!');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (isBulkMode) {
      const names = policyViolationsNames
        .split('\n')
        .map((name: string) => name.trim())
        .filter((name: string) => name.length > 0);

      if (names.length === 0) {
        toast.error('At least one name is required');
        return;
      }

      setLoading(true);

      try {
        let successCount = 0;
        let errorCount = 0;

        for (const name of names) {
          try {
            await PolicyViolationsService(token).create({ name });
            successCount++;
          } catch (err: any) {
            errorCount++;
            console.error(`Error creating policy violation "${name}":`, err);
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} policy violation${successCount > 1 ? 's' : ''} created successfully!`);
          listData();
        }

        if (errorCount > 0) {
          toast.error(`${errorCount} policy violation${errorCount > 1 ? 's' : ''} failed to create`);
        }

        setPolicyViolationsNames('');
        setIsBulkMode(false);
        setShowForm(false);
        setEditId(null);
      } catch (e: any) {
        const message = String(e?.response?.data?.message || 'Create error!');
        toast.error(message);
      } finally {
        setLoading(false);
      }
    } else {
      if (!selectedFile) {
        toast.error('File is required');
        return;
      }

      setLoading(true);

      const data = { name: policyViolationsName };

      try {
        const result = await PolicyViolationsService(token).create(data);

        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile as any);
          await PolicyViolationsService(token).file(result?.data?.id, formData);
        }

        toast.success(`Saved successfully!`);
        listData();
        setPolicyViolationsName('');
        setSelectedFile(null);
        setShowForm(false);
        setEditId(null);
      } catch (e: any) {
        const message = String(e?.response?.data?.message || 'Create error!');
        toast.error(message);
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteSettings = async (e: React.FormEvent, id: string) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    try {
      await PolicyViolationsService(token).delete(id);

      toast.success(`Deleted successfully!`);
      listData();
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Delete error!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Row>
        <Col>
          <UncontrolledDropdown>
            <DropdownToggle caret color="primary">
              <i className="fas fa-plus mr-2" />
              Create New
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem
                onClick={() => {
                  setEditId(null);
                  setPolicyViolationsName('');
                  setSelectedFile(null);
                  setShowForm(true);
                  setIsBulkMode(false);
                }}
              >
                New
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  setEditId(null);
                  setPolicyViolationsNames('');
                  setShowForm(true);
                  setIsBulkMode(true);
                }}
              >
                Bulk
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </Col>
      </Row>
      <Table className="align-items-center table-flush mt-2" responsive>
        <tbody>
          <tr>
            <th>Name</th>
            <th>Action</th>
          </tr>
          {policyViolationsList?.length === 0 && (
            <tr>
              <td colSpan={2} className="text-center">
                Data not found
              </td>
            </tr>
          )}
          {policyViolationsList.map((violation: any) => (
            <tr key={`policyViolations-modal-${violation.id}`}>
              <td>{violation.name}</td>
              <td>
                <i
                  className="fas fa-edit text-primary mr-2"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    setEditId(violation.id);
                    setPolicyViolationsName(violation.name);
                    setShowForm(true);
                    setIsBulkMode(false);
                  }}
                />
                {violation.file && (
                  <Button
                    style={{
                      border: 'none',
                      boxShadow: 'none',
                      background: '#FFFFFF',
                      padding: 0,
                      alignItems: 'center',
                      transition: 'filter 0.3s ease',
                      filter: 'brightness(1)',
                      color: '#416adb',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(1.2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.filter = 'brightness(1)')}
                  >
                    <a href={violation.file} target="_blank" className="d-flex flex-column align-items-center">
                      <i className="fas fa-download" />
                    </a>
                  </Button>
                )}
                <i
                  className="far fa-trash-alt text-danger ml-2"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => deleteSettings(e, violation.id)}
                ></i>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ModalContent
        title={
          isBulkMode
            ? 'Bulk Create Policy Violations'
            : editId
            ? 'Edit Policy Violation'
            : 'Create New Policy Violation'
        }
        modal={showForm}
        toggle={() => {
          setShowForm(false);
          setPolicyViolationsName('');
          setPolicyViolationsNames('');
          setSelectedFile(null);
          setIsBulkMode(false);
          setEditId(null);
        }}
        size="md"
      >
        <Form onSubmit={handleSubmit}>
          <Row>
            {isBulkMode ? (
              <Col lg="12">
                <FormGroup>
                  <Label className="form-control-label">Names (one per line)</Label>
                  <Input
                    type="textarea"
                    rows={8}
                    value={policyViolationsNames}
                    onChange={(e) => setPolicyViolationsNames(e.target.value)}
                    placeholder="Enter policy violation names, one per line..."
                    required
                  />
                </FormGroup>
              </Col>
            ) : (
              <>
                <Col lg="12">
                  <FormGroup>
                    <Label className="form-control-label">Name</Label>
                    <Input
                      value={policyViolationsName}
                      onChange={(e) => setPolicyViolationsName(e.target.value)}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col lg="12">
                  <FormGroup>
                    <Label className="form-control-label">Attach File</Label>
                    <div className="d-flex align-items-center">
                      <Button color="primary" onClick={handleCustomButtonClick} style={{ width: 160 }}>
                        Select File
                      </Button>
                      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
                      {selectedFile && (
                        <div className="d-flex align-items-center ml-3">
                          <span className="mr-2">Selected: {selectedFile.name}</span>
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedFile(null);
                            }}
                          >
                            <i className="fas fa-times" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </>
            )}
            <Col md="12" className="text-right mt-4">
              <Button
                color="secondary"
                size="lg"
                style={{ minWidth: '100px', marginRight: '10px' }}
                onClick={(e) => {
                  e.preventDefault();
                  setShowForm(false);
                  setPolicyViolationsName('');
                  setPolicyViolationsNames('');
                  setSelectedFile(null);
                  setIsBulkMode(false);
                  setEditId(null);
                }}
              >
                Cancel
              </Button>
              <Button color="primary" type="submit" size="lg" style={{ minWidth: '100px' }} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Col>
          </Row>
        </Form>
      </ModalContent>
    </>
  );
};

export default PolicyViolationsForm;
